/** @jsxImportSource @emotion/react */
import { css, keyframes } from '@emotion/react';
import { ButtonLink } from '../common';
import { colors, typography } from '../../styles/theme';

const float1 = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  25% { transform: translate(10px, -15px) rotate(5deg); }
  50% { transform: translate(-5px, -25px) rotate(-3deg); }
  75% { transform: translate(15px, -10px) rotate(3deg); }
`;

const float2 = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  33% { transform: translate(-15px, -20px) rotate(-5deg); }
  66% { transform: translate(10px, -10px) rotate(5deg); }
`;

const float3 = keyframes`
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(0, -20px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

const heroStyles = css`
  background: linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[700]} 40%, ${colors.primary[800]} 100%);
  color: white;
  padding: 4rem 1rem 5rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  @media (min-width: 768px) {
    padding: 6rem 2rem 7rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, ${colors.primary[500]}15 0%, transparent 50%);
    animation: ${pulse} 10s ease-in-out infinite;
  }
`;

const floatingElementsStyles = css`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
`;

const floatingCircle1 = css`
  position: absolute;
  top: 15%;
  left: 8%;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.08);
  animation: ${float1} 8s ease-in-out infinite;

  @media (min-width: 768px) {
    width: 120px;
    height: 120px;
  }
`;

const floatingCircle2 = css`
  position: absolute;
  top: 60%;
  right: 10%;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
  animation: ${float2} 10s ease-in-out infinite;

  @media (min-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const floatingCircle3 = css`
  position: absolute;
  bottom: 20%;
  left: 15%;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  animation: ${float3} 6s ease-in-out infinite;

  @media (min-width: 768px) {
    width: 60px;
    height: 60px;
  }
`;

const floatingRoad = css`
  position: absolute;
  top: 25%;
  right: 5%;
  opacity: 0.06;
  animation: ${float1} 12s ease-in-out infinite;

  svg {
    width: 80px;
    height: 80px;
  }

  @media (min-width: 768px) {
    svg {
      width: 140px;
      height: 140px;
    }
  }
`;

const floatingCar = css`
  position: absolute;
  bottom: 15%;
  right: 20%;
  opacity: 0.05;
  animation: ${float2} 9s ease-in-out infinite;

  svg {
    width: 60px;
    height: 60px;
  }

  @media (min-width: 768px) {
    svg {
      width: 100px;
      height: 100px;
    }
  }
`;

const dotPattern = css`
  position: absolute;
  top: 10%;
  right: 15%;
  display: grid;
  grid-template-columns: repeat(4, 8px);
  gap: 12px;
  opacity: 0.08;

  span {
    width: 4px;
    height: 4px;
    background: white;
    border-radius: 50%;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const heroContentStyles = css`
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
`;

const heroTitleStyles = css`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.extrabold};
  color: white;
  margin-bottom: 1rem;
  line-height: 1.2;
  letter-spacing: -0.02em;

  @media (min-width: 768px) {
    font-size: ${typography.fontSize['5xl']};
  }
`;

const heroSubtitleStyles = css`
  font-size: ${typography.fontSize.lg};
  color: ${colors.primary[100]};
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: 768px) {
    font-size: ${typography.fontSize.xl};
  }
`;

const heroButtonsStyles = css`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 320px;
  margin: 0 auto;

  @media (min-width: 480px) {
    flex-direction: row;
    justify-content: center;
    max-width: none;
    gap: 1rem;
  }
`;

export default function HeroSection() {
  return (
    <section css={heroStyles}>
      <div css={floatingElementsStyles} aria-hidden="true">
        <div css={floatingCircle1} />
        <div css={floatingCircle2} />
        <div css={floatingCircle3} />
        <div css={floatingRoad}>
          <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 80 L50 20 L80 80" stroke="white" strokeWidth="3" strokeLinecap="round" fill="none" />
            <line x1="50" y1="35" x2="50" y2="45" stroke="white" strokeWidth="2" strokeDasharray="4 4" />
            <line x1="50" y1="55" x2="50" y2="65" stroke="white" strokeWidth="2" strokeDasharray="4 4" />
          </svg>
        </div>
        <div css={floatingCar}>
          <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="15" y="45" width="70" height="25" rx="8" stroke="white" strokeWidth="3" />
            <path d="M25 45 L35 25 L65 25 L75 45" stroke="white" strokeWidth="3" strokeLinecap="round" />
            <circle cx="30" cy="72" r="7" stroke="white" strokeWidth="3" />
            <circle cx="70" cy="72" r="7" stroke="white" strokeWidth="3" />
          </svg>
        </div>
        <div css={dotPattern}>
          {Array.from({ length: 16 }).map((_, i) => (
            <span key={i} />
          ))}
        </div>
      </div>

      <div css={heroContentStyles}>
        <h1 css={heroTitleStyles}>
          Yolda Kaldığınızda<br />Yanınızdayız
        </h1>
        <p css={heroSubtitleStyles}>
          Çekici, vinç, yol yardım ve nakliye hizmetlerinde Türkiye'nin güvenilir adresi.
          7/24 profesyonel destek.
        </p>
        <div css={heroButtonsStyles}>
          <ButtonLink href="https://yolsepetigo.com" variant="secondary" size="lg">
            Hemen Yardım Al
          </ButtonLink>
          <ButtonLink to="/nasil-kullanilir" variant="outlineWhite" size="lg">
            Nasıl Kullanılır?
          </ButtonLink>
        </div>
      </div>
    </section>
  );
}
